import React, { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { AlertAnimation } from '@eika/alert-animation';
import { Button } from '@eika/button';
import { ButtonWithSpinner } from '@eika/button-with-spinner';
import { InfoCard, WarningCard } from '@eika/card';
import { Link } from '@eika/link';
import { Loader } from '@eika/loader';
import { RichIcon, RichIconTypes } from '@eika/rich-icon';

import { ErrorCardWrapper } from '../../../../components/shared/ErrorCardWrapper';
import { hasSigningError } from '../../../../components/transaction/Payment/PaymentForm';
import { useConfig } from '../../../../defaultConfig';
import useTranslation, { formatJsx } from '../../../../hooks/useTranslation';
import { startAvtalegiroMove } from '../../../../services/avtaleGiroService';
import SigningContextFlow from '../../../../signingContext/SigningContextFlow';
import { useSigningContext } from '../../../../signingContext/signingContext';
import { paymentTrackIDs } from '../../../../utils/gtm/trackIDs/payment';
import { useAvtaleGiroMoveContext } from '../../context/AvtaleGiroMoveContext';
import ROUTES from '../../routes';

const MoveAvtaleGiroInitialSigning = () => {
    const { avtalegiroLink } = useConfig();

    const navigate = useNavigate();
    const translated = useTranslation();

    const {
        startSigning,
        handleCancelSigningFlow,
        signingCompleted,
        dynamicLinkId,
        signingOperationPending,
        signingStatus,
        signingError,
    } = useSigningContext(startAvtalegiroMove);

    const { accounts } = useAvtaleGiroMoveContext();

    useEffect(() => {
        if (signingCompleted && dynamicLinkId) {
            navigate(ROUTES.chooseDebitAccount);
        }
    }, [dynamicLinkId, navigate, signingCompleted]);

    const clearAndRetrySigning = useCallback(() => {
        handleCancelSigningFlow();
        startSigning();
    }, [handleCancelSigningFlow, startSigning]);

    if (accounts.isLoading)
        return (
            <div className="move-avtalegiro-loading-wrapper">
                <Loader />
            </div>
        );

    if (accounts.error)
        return (
            <ErrorCardWrapper
                exception={accounts.error}
                iconType={RichIconTypes.NETTVERKSBRUDD_HARE}
                title={translated.errors.somethingWentWrong}
                defaultError={translated.moveAvtaleGiro.errors.accountsCouldNotBeFetched}
            >
                {avtalegiroLink && (
                    <Link linkType="button-positive" href={avtalegiroLink.url}>
                        {translated.goBack}
                    </Link>
                )}
            </ErrorCardWrapper>
        );

    if (accounts.data.length === 0 && !accounts.error && !accounts.isLoading) {
        return (
            <InfoCard>
                <RichIcon maxHeight={160} minHeight={160} type={RichIconTypes.HAAND_HEIHEI} />
                <InfoCard.TitleH3>{translated.moveAvtaleGiro.errors.noValidAccounts}</InfoCard.TitleH3>
                <p>{translated.moveAvtaleGiro.errors.ifThisIsWrongContactUs}</p>
            </InfoCard>
        );
    }

    if (signingError || hasSigningError(signingStatus)) {
        return (
            <WarningCard
                className="avtalegiro-result-warning-card"
                title={translated.moveAvtaleGiro.rejected.heading}
                isModal
                icon={<AlertAnimation />}
            >
                <p className="avtalegiro-result-warning-card__description">
                    {translated.moveAvtaleGiro.rejected.description}
                </p>
                <div className="move-avtalegiro__action-button-wrapper">
                    <Button className="warning__button" onClick={clearAndRetrySigning}>
                        {translated.tryAgain}
                    </Button>
                    {avtalegiroLink && <Link href={avtalegiroLink.url}>{translated.cancel}</Link>}
                </div>
            </WarningCard>
        );
    }

    return (
        <SigningContextFlow onClosePayment={handleCancelSigningFlow}>
            <InfoCard skipBackground>
                <RichIcon maxHeight={160} minHeight={160} type={RichIconTypes.STANDARISERING} />

                <p>{translated.moveAvtaleGiro.whenYouStartTheProcess}</p>
                <p>{translated.moveAvtaleGiro.quickAndIntuitive}</p>
                <p>
                    {formatJsx(
                        translated.moveAvtaleGiro.notaBeneDescription,
                        <strong>{translated.moveAvtaleGiro.notaBene}</strong>,
                    )}
                </p>
            </InfoCard>
            <section className="move-avtalegiro__action-button-wrapper">
                <ButtonWithSpinner
                    onClick={() => startSigning()}
                    showSpinner={signingOperationPending}
                    data-track-id={paymentTrackIDs.avtalegiro.moveAvtalegiros}
                    data-track-linkname={translated.moveAvtaleGiro.startProcess}
                >
                    {translated.moveAvtaleGiro.startProcess}
                </ButtonWithSpinner>
                {avtalegiroLink && <Link href={avtalegiroLink.url}>{translated.cancel}</Link>}
            </section>
        </SigningContextFlow>
    );
};

export default MoveAvtaleGiroInitialSigning;
